import { useCallback, useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import { useAtomValue, useSetAtom } from 'jotai'
import { isAxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import { apis } from '@/modules/apiClients'
import { ClientAnalytics } from '@/modules/clientAnalytics'
import {
  isSavingWorkAtom,
  lastSaveWorkDateAtom,
  workSaveRetryNonceAtom,
  workSaveStatusAtom,
} from '@/store/saveWorkAtoms'
import type { ChallengeService } from '@duck/api-definitions'

const DEBOUNCE_MS = 1500
const MAX_WAIT_MS = 20_000

type WorkStatePatch = Partial<ChallengeService.WorkState>

/**
 * A save failed at the transport level — no HTTP response ever came back. Covers
 * axios "Network Error" (status 0), request timeouts ("timeout exceeded",
 * ECONNABORTED), DNS/connection-refused, and the browser reporting offline. All of
 * these are connectivity blips that self-heal on the next save, so we treat them as
 * "offline" (not "Save failed") and don't log them to Sentry. A real server failure
 * carries an `err.response` and is handled separately.
 */
const isOfflineError = (err: unknown): boolean =>
  (typeof navigator !== 'undefined' && navigator.onLine === false) ||
  (isAxiosError(err) && !err.response)

/**
 * Structured detail for a *real* (non-offline) save failure, so the log is actually
 * useful instead of an empty `{}`. Captures the HTTP status (e.g. 413, 500), which
 * fields were being saved (solution/jNotebook/tabViews/notes), and the payload size —
 * the size is the key clue for "payload too large".
 */
const describeSaveError = (err: unknown, challengeId: string, patch: WorkStatePatch, serialized: string) => {
  const ctx = { challengeId, fields: Object.keys(patch), bytes: serialized.length }
  if (isAxiosError(err)) {
    const data = err.response?.data
    return {
      ...ctx,
      code: err.code,
      status: err.response?.status,
      statusText: err.response?.statusText,
      responseData: typeof data === 'string' ? data.slice(0, 500) : data,
      message: err.message,
    }
  }
  return { ...ctx, message: err instanceof Error ? err.message : String(err) }
}

/**
 * Shared throttled saver for workspace state. Use for any save-on-change site
 * (notebook, solution, notes, tab views).
 *
 * Behavior:
 * - Trailing debounce of 1.5s after the last `schedule` call.
 * - Hard maxWait of 20s during continuous activity (e.g. nonstop typing).
 * - Hash-based dedup: identical patches don't re-send.
 * - Best-effort flush on `beforeunload` (axios fire-and-forget — browsers
 *   commonly let the request complete) and on component unmount.
 */
export function useDebouncedWorkSaver(challengeId: string | null | undefined) {
  const setIsSaving = useSetAtom(isSavingWorkAtom)
  const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
  const setSaveStatus = useSetAtom(workSaveStatusAtom)
  const retryNonce = useAtomValue(workSaveRetryNonceAtom)

  const challengeIdRef = useRef(challengeId)
  challengeIdRef.current = challengeId

  const lastSavedHashRef = useRef<string | null>(null)
  const pendingRef = useRef<{ patch: WorkStatePatch; serialized: string } | null>(null)
  // Latest patch this instance has ever scheduled (its current domain value), kept even
  // after a successful save. A manual retry force-resends this so the user's whole state
  // is re-pushed — not just the dirty/failed delta.
  const lastPatchRef = useRef<{ patch: WorkStatePatch; serialized: string } | null>(null)

  const performSave = useCallback(async () => {
    const id = challengeIdRef.current
    const pending = pendingRef.current
    if (!id || !pending) return
    if (pending.serialized === lastSavedHashRef.current) {
      pendingRef.current = null
      return
    }
    const { patch, serialized } = pending
    pendingRef.current = null
    setIsSaving(true)
    setSaveStatus('saving')
    try {
      await apis.challengeService.saveWork({
        challengeId: id,
        workState: patch as ChallengeService.WorkState,
      })
      lastSavedHashRef.current = serialized
      setLastSaveDate(new Date())
      setSaveStatus('saved')
    } catch (err) {
      // Offline/connectivity blips are benign and self-heal on the next save — don't
      // log them (keeps Sentry quiet); surface them as "offline" in the UI instead.
      if (isOfflineError(err)) {
        setSaveStatus('offline')
      } else {
        setSaveStatus('error')
        const failureDetail = describeSaveError(err, id, patch, serialized)
        console.error('Failed to save workState', failureDetail)
        Sentry.captureException(err, { extra: failureDetail })
        // Surface real (non-offline) save failures in product analytics too.
        ClientAnalytics.track('workspace:save:failed', failureDetail)
      }
      // Restore pending so a future schedule() retries automatically
      if (!pendingRef.current) {
        pendingRef.current = { patch, serialized }
      }
    } finally {
      setIsSaving(false)
    }
  }, [setIsSaving, setLastSaveDate, setSaveStatus])

  // Manual retry: force-resend this instance's latest known state, even if it was
  // already saved (so the full workState is re-pushed, not just a failed delta).
  // Instances that never scheduled anything no-op. Bypasses the hash dedup on purpose.
  const forceResend = useCallback(async () => {
    const id = challengeIdRef.current
    const latest = lastPatchRef.current
    if (!id || !latest) return
    const { patch, serialized } = latest
    setIsSaving(true)
    setSaveStatus('saving')
    try {
      await apis.challengeService.saveWork({
        challengeId: id,
        workState: patch as ChallengeService.WorkState,
      })
      lastSavedHashRef.current = serialized
      pendingRef.current = null
      setLastSaveDate(new Date())
      setSaveStatus('saved')
    } catch (err) {
      if (isOfflineError(err)) {
        setSaveStatus('offline')
      } else {
        setSaveStatus('error')
        const failureDetail = describeSaveError(err, id, patch, serialized)
        console.error('Failed to save workState', failureDetail)
        Sentry.captureException(err, { extra: failureDetail })
        // Surface real (non-offline) save failures in product analytics too.
        ClientAnalytics.track('workspace:save:failed', failureDetail)
      }
      // Keep it pending so the next edit retries automatically too.
      if (!pendingRef.current) {
        pendingRef.current = { patch, serialized }
      }
    } finally {
      setIsSaving(false)
    }
  }, [setIsSaving, setLastSaveDate, setSaveStatus])

  // When the retry nonce bumps, every active saver re-sends its latest state.
  // Skip the initial (nonce === 0) render.
  useEffect(() => {
    if (retryNonce === 0) return
    void forceResend()
  }, [retryNonce, forceResend])

  const debouncedFlush = useMemo(
    () =>
      debounce(performSave, DEBOUNCE_MS, {
        maxWait: MAX_WAIT_MS,
        leading: false,
        trailing: true,
      }),
    [performSave],
  )

  const schedule = useCallback(
    (patch: WorkStatePatch) => {
      if (!challengeIdRef.current) return
      const serialized = JSON.stringify(patch)
      // Always remember the latest value for this domain (used by manual retry),
      // even when dedup decides there's nothing new to send.
      lastPatchRef.current = { patch, serialized }
      if (serialized === lastSavedHashRef.current) {
        pendingRef.current = null
        return
      }
      pendingRef.current = { patch, serialized }
      debouncedFlush()
    },
    [debouncedFlush],
  )

  // Best-effort flush on tab close. Cancel pending debounce, fire one immediate
  // axios request without awaiting; modern browsers commonly let it complete.
  useEffect(() => {
    const handler = () => {
      const id = challengeIdRef.current
      const pending = pendingRef.current
      if (!id || !pending) return
      if (pending.serialized === lastSavedHashRef.current) return
      debouncedFlush.cancel()
      lastSavedHashRef.current = pending.serialized
      const { patch } = pending
      pendingRef.current = null
      apis.challengeService
        .saveWork({ challengeId: id, workState: patch as ChallengeService.WorkState })
        .catch(() => {})
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [debouncedFlush])

  // Flush any pending save on unmount.
  useEffect(() => {
    return () => {
      debouncedFlush.flush()
    }
  }, [debouncedFlush])

  const cancel = useCallback(() => {
    debouncedFlush.cancel()
    pendingRef.current = null
  }, [debouncedFlush])

  return { schedule, flush: debouncedFlush.flush, cancel }
}
